/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { List, Spacer } from "@cd2/components"
import { H4 } from "@cd2/text"
import { useQuery } from "@tanstack/react-query"
import React, { useState } from "react"
import Btn from "../../comps/Button"
import { useApi } from "../../hooks/useApi"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { isProductExported } from "../../stories/quoteOrders/quoteOrderUtils"
import { IRecord } from "../../types/schema"
import { brandColors, colors, themeColors } from "../EOSThemeProvider"
import { useGlobalState } from "../GlobalStateProvider"
import { KeyValueList } from "../KeyValueList"
import { FloatingBox } from "../Misc/FloatingBox"
import { ExportToSageButton } from "./ExportToSageButton"

export function SageApiExportProductToSage({
  productId,
  afterExport,
  compact,
}: {
  productId: string
  afterExport(): any
  compact?: boolean
}) {
  const skeem = window.skeem
  const alert = useSiteAlerts()
  const api = useApi()
  const { currentUser } = useGlobalState()

  const [rawError, setRawError] = useState("")

  const {
    data,
    isPending,
    refetch,
    error: queryError,
  } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () =>
      skeem.fetch2<
        IRecord<
          "products",
          "raw_sage_id" | "sage_export_status" | "sage_exported_at" | "sage_export_completed_at"
        >
      >({
        first: {
          name: "products",
          attributes: [
            "raw_sage_id",
            "sage_export_status",
            "sage_exported_at",
            "sage_export_completed_at",
          ],
          matches: { id: productId },
        },
      }),
  })

  const error = rawError || queryError?.message

  const handleExport = useAsyncCallback(async () => {
    if (!data) {
      alert.genericError("No data")
      return
    }

    const exportResponse = await api.exportProductToSage(productId)

    if ("error" in exportResponse) {
      alert.genericError(exportResponse.error)
      console.error("Error exporting product to Sage", exportResponse.error)
      setRawError(exportResponse.error ?? "Error exporting product to Sage")
      return
    }

    refetch()
    await afterExport()
    alert.genericSuccess("Product exported")
  })

  return (
    <div>
      <FloatingBox
        css={
          compact
            ? css`
                ${FloatingBox.vars.childPadding}: 0;
                border: none;
              `
            : css`
                ${FloatingBox.vars.borderColor}: ${colors.sage};
                text-align: left;
                width: 100%;
              `
        }
      >
        <List>
          {!compact && (
            <KeyValueList
              labelWidth={140}
              data={[
                {
                  label: "Product Sage ID",
                  value: data?.raw_sage_id || "Not Exported",
                },
                {
                  label: "Last Exported",
                  value: { date: data?.sage_exported_at, format: "dd/MM/yyyy - HH:mm" },
                },
              ]}
              inline
            />
          )}
          {error && (
            <FloatingBox
              color={themeColors.lightOrange}
              css={css`
                white-space: normal;
              `}
            >
              <b>Cannot export to Sage: </b>
              {error}
            </FloatingBox>
          )}
          <div>
            <ExportToSageButton
              handleExport={handleExport}
              loading={isPending || handleExport.running}
              invalid={!!queryError}
            />
            {currentUser.isSuperAdmin && process.env.NODE_ENV === "development" && !compact && (
              <React.Fragment>
                <Spacer />
                <Btn
                  onClick={async () => {
                    if (
                      confirm(
                        "This will clear the sage attributes AND the raw_sage_id, are you sure?",
                      )
                    ) {
                      await skeem.mutate({
                        products: {
                          update: {
                            id: productId,
                            raw_sage_id: null,
                            sage_export_status: "",
                            sage_export_json: {},
                            sage_exported_at: null,
                          },
                        },
                      })
                      await refetch()
                    }
                  }}
                  label="Debug: Unexport Product"
                />
              </React.Fragment>
            )}
          </div>
        </List>
      </FloatingBox>
    </div>
  )
}

export function UnexportedProductPromptWithButton({
  product,
  afterExport,
}: {
  product?: IRecord<"products", "sage_export_status" | "sage_id">
  afterExport?: () => any
}) {
  const { currentUser } = useGlobalState()

  if (!product) {
    return null
  }
  if (isProductExported(product)) {
    return null
  }

  return (
    <FloatingBox
      color={brandColors.orangeLightBackground}
      css={css`
        margin-top: 4px;
      `}
    >
      <H4 blockSpacing={0}>This product has not been exported to Sage</H4>
      <Spacer />
      {currentUser.canExportNewProducts && (
        <div
          css={css`
            width: max-content;
          `}
        >
          <SageApiExportProductToSage
            productId={product.id}
            afterExport={afterExport || console.log}
            compact
          />
        </div>
      )}
    </FloatingBox>
  )
}
